// File: app/src/components/session/LogWorkoutDialog.js
//
// Record training that happened outside Khronos (#77).
//
// The reason this exists is not bookkeeping — it is that the planner reasons
// about the next session from the last few days, and a week of gym sessions it
// never saw makes it plan as though the user had been resting. So the form asks
// for exactly what the history prompt can use and nothing more: when, what
// kind, how long, and (for a lift) which movements.
//
// Every field past those is optional on purpose. "I did legs on Tuesday for
// about an hour" is a complete and useful entry; demanding set-by-set recall
// for a session three days gone is how a logging feature stops being used.

import React, { useRef, useState } from "react";
import UnitToggle from "../ui/UnitToggle";
import { useWeightUnit } from "../../contexts/WeightUnitContext";
import { fromDisplayWeight } from "../../utils/units";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { cn } from "lib/utils";
import { Loader2, Plus, Sparkles, Trash2, Undo2 } from "lucide-react";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import usePlanEditor from "../../hooks/usePlanEditor";
import EffortMeter from "../workout/EffortMeter";
import {
  ACTIVITY_TYPES,
  activityTracksDistance,
  adoptParsedWorkout,
  buildLoggedSession,
  buildLoggedSessionFromPlan,
} from "../../utils/loggedSession";

const FIELD_LABEL_CLASS =
  "text-[10px] text-muted-foreground uppercase tracking-widest font-semibold";

const INVALID_INPUT_CLASS =
  "border-destructive focus-visible:ring-destructive";

// `datetime-local` wants a local "YYYY-MM-DDTHH:mm" with no zone. Building it
// from the parts (rather than toISOString, which converts to UTC) is what keeps
// an 8pm session from being offered back as 10am the next day.
const toLocalInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

// Anchored to today, and idempotent: tapping "2 days ago" twice still means two
// days ago. Shifting the CURRENT value instead made the chips cumulative — two
// taps on "Yesterday" silently meant the day before that — which is no way to
// set a date you are trying to get right.
const daysAgoValue = (days, now = new Date()) => {
  const base = new Date(now);
  base.setDate(base.getDate() - days);
  return toLocalInputValue(base);
};

const DAY_CHIPS = [
  { days: 1, label: "Yesterday" },
  { days: 2, label: "2 days ago" },
  { days: 3, label: "3 days ago" },
];

const emptyExercise = () => ({ label: "", sets: "", reps: "", weight: "" });

// Name on its own line, figures beneath. Four inputs plus a delete button on
// one row left about a hundred pixels for the exercise name on a phone, which
// is not enough to read "Romanian Deadlift" in. The numbers are a natural
// group and sit as one, under a header row that names them — placeholders
// alone vanish the moment you type into them, and a bare grid of three boxes
// gives no clue which is which.
const ExerciseRow = ({ row, onChange, onRemove, canRemove, showHeadings, weightUnit = "kg" }) => (
  <div className="rounded-lg bg-muted/30 p-2 space-y-1.5">
    <div className="flex items-center gap-1.5">
      <Input
        value={row.label}
        onChange={(e) => onChange({ ...row, label: e.target.value })}
        placeholder="Exercise"
        aria-label="Exercise name"
        className="h-8 flex-1 min-w-0 text-sm"
      />
      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        aria-label="Remove exercise"
        className="shrink-0 p-1 text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:hover:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>

    <div className="grid grid-cols-3 gap-1.5 pr-7">
      {[
        { key: "sets", label: "Sets", step: undefined, mode: "numeric" },
        { key: "reps", label: "Reps", step: undefined, mode: "numeric" },
        { key: "weight", label: weightUnit, step: weightUnit === "lb" ? "1" : "0.5", mode: "decimal" },
      ].map((field) => (
        <div key={field.key} className="space-y-0.5">
          {showHeadings && (
            <span className="block text-[9px] uppercase tracking-widest text-muted-foreground">
              {field.label}
            </span>
          )}
          <Input
            value={row[field.key]}
            onChange={(e) => onChange({ ...row, [field.key]: e.target.value })}
            type="number"
            min="0"
            step={field.step}
            inputMode={field.mode}
            placeholder={field.label}
            aria-label={field.key === "weight" ? (weightUnit === "lb" ? "Weight in pounds" : "Weight in kilograms") : field.label}
            className="h-8 w-full px-1.5 text-center text-sm tabular-nums"
          />
        </div>
      ))}
    </div>
  </div>
);

// The budget covers the whole interpretation, INCLUDING the token-escalation
// retries a long workout triggers — three requests, not one. Ninety seconds was
// enough for a single call and would have cut an escalating one off partway.
// The proxy's 120s limit is per-request inactivity, so a sequence is fine by it.
const PARSE_TIMEOUT_MS = 180000;

// When to admit it is taking a while. A long description genuinely takes longer
// (the response is retried at a higher token limit), and a button that has said
// "Reading it…" for forty seconds with no other sign of life reads as a hang.
const PARSE_SLOW_AFTER_MS = 20000;

const MODES = [
  { value: "describe", label: "Describe it" },
  { value: "form", label: "Fill it in" },
];

/**
 * The two ways in, as explicit tabs.
 *
 * This was a SegmentedToggle, whose off state sets no text colour of its own —
 * it inherits, and in this dialog it came out near-invisible, so the describe
 * flow (much the better of the two) looked like it did not exist at all. Both
 * states carry their own colour here, and the active one is filled rather than
 * merely tinted.
 */
const ModeTabs = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-1 rounded-lg border border-border/60 bg-background/40 p-1">
    {MODES.map((m) => {
      const active = value === m.value;
      return (
        <button
          key={m.value}
          type="button"
          onClick={() => onChange(m.value)}
          aria-pressed={active}
          className={cn(
            "h-9 rounded-md text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            active
              ? "bg-primary/20 text-primary font-semibold"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {m.label}
        </button>
      );
    })}
  </div>
);

/**
 * The eight activity kinds as one selection.
 *
 * Wrapping pills rather than a fixed grid. As a 2-column grid these ran to four
 * rows and about a third of a phone screen — an enormous amount of room for the
 * least consequential choice in the dialog, pushing what you came to enter
 * below the fold. Sized to their labels they fold into two rows.
 *
 * (They were briefly two segmented controls bound to one value, which was worse
 * still: picking "Swim" from the second row left the first with nothing
 * highlighted, so it read as two broken controls rather than one choice.)
 */
const ActivityPicker = ({ value, onChange }) => (
  <div role="radiogroup" aria-label="Activity" className="flex flex-wrap gap-1.5">
    {ACTIVITY_TYPES.map((activity) => {
      const active = value === activity.value;
      return (
        <button
          key={activity.value}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(activity.value)}
          className={cn(
            "h-8 rounded-full border px-3 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            active
              ? "border-primary/50 bg-primary/20 text-primary font-medium"
              : "border-border/50 bg-background/40 text-muted-foreground hover:text-foreground",
          )}
        >
          {activity.label}
        </button>
      );
    })}
  </div>
);

const LogWorkoutDialog = ({
  open,
  onOpenChange,
  onSave,
  onParse,
  llmSettings,
}) => {
  // Free-text mode (#77). Describing a session out loud is how people actually
  // remember it — "three sets of 20 burpees, then bench supersetted with
  // deadlifts" — and the structure the app needs is a transcription problem the
  // LLM is already good at. What comes back is a DRAFT: it lands in the normal
  // editable plan view so a mis-parse (sets and reps the wrong way round is the
  // classic) is visible and fixable before anything is saved.
  const [mode, setMode] = useState("describe");
  const [description, setDescription] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parseSlow, setParseSlow] = useState(false);
  const [parsedPlan, setParsedPlan] = useState(null);
  const [durationEstimated, setDurationEstimated] = useState(false);

  const [activityType, setActivityType] = useState("strength");
  const [title, setTitle] = useState("");
  const [startedAt, setStartedAt] = useState(() =>
    toLocalInputValue(new Date()),
  );
  const [durationMinutes, setDurationMinutes] = useState("");
  const [distanceKm, setDistanceKm] = useState("");
  const [avgHr, setAvgHr] = useState("");
  const [rating, setRating] = useState(null);
  const [notes, setNotes] = useState("");
  const [exercises, setExercises] = useState(() => [emptyExercise()]);
  const preferredUnit = useWeightUnit();
  const [weightUnit, setWeightUnit] = useState(preferredUnit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Which input a refused save was about. The message alone sat at the foot of
  // a scrolling form, usually out of sight, so a blank duration made "Save to
  // history" look like it simply did nothing. The field itself now turns red
  // and takes focus, which scrolls it into view.
  const [invalidField, setInvalidField] = useState(null);
  const fieldRefs = { duration: useRef(null), startedAt: useRef(null) };

  const isStrength = activityType === "strength";
  const tracksDistance = activityTracksDistance(activityType);
  // The when/how-long/effort fields belong to both routes, but there is nothing
  // to attach them to until the description has been interpreted.
  const showDetails = !!parsedPlan || mode === "form";

  // The parsed tree is edited in place through the shared plan editor, so the
  // preview gets the same add/remove/reorder/detail affordances as any other
  // plan view rather than a second, weaker editor built just for this.
  const { planDisplayProps } = usePlanEditor(setParsedPlan);

  const reset = () => {
    setMode("describe");
    setDescription("");
    setParsedPlan(null);
    setDurationEstimated(false);
    setActivityType("strength");
    setTitle("");
    setStartedAt(toLocalInputValue(new Date()));
    setDurationMinutes("");
    setDistanceKm("");
    setAvgHr("");
    setRating(null);
    setNotes("");
    setExercises([emptyExercise()]);
    setError(null);
    setInvalidField(null);
  };

  const interpret = async () => {
    setError(null);
    if (!description.trim()) {
      setError("Describe the workout first.");
      return;
    }
    setParsing(true);
    setParseSlow(false);
    // Bounded wait. Without this a stalled provider leaves the button spinning
    // on "Reading it…" indefinitely, which reads as the app having hung.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), PARSE_TIMEOUT_MS);
    const slowTimer = setTimeout(() => setParseSlow(true), PARSE_SLOW_AFTER_MS);
    try {
      const result = await onParse(description, llmSettings, abort.signal);
      const adopted = result ? adoptParsedWorkout(result) : null;
      if (!adopted) {
        setError(
          "Couldn't make sense of that. Try naming the exercises, sets and reps — or fill it in by hand.",
        );
        return;
      }
      setParsedPlan(adopted.plan);
      const f = adopted.fields;
      setTitle(f.title);
      setActivityType(f.activityType);
      setStartedAt(toLocalInputValue(f.startedAt));
      setDurationMinutes(f.durationMinutes ? String(f.durationMinutes) : "");
      setDurationEstimated(f.durationEstimated);
      if (f.rating != null) setRating(f.rating);
      if (f.notes) setNotes(f.notes);
    } catch (err) {
      setError(
        err?.name === "AbortError"
          ? "That took too long to come back. Try splitting it into two entries, or fill it in by hand."
          : err.message || "Could not reach the model.",
      );
    } finally {
      clearTimeout(timer);
      clearTimeout(slowTimer);
      setParseSlow(false);
      setParsing(false);
    }
  };

  const close = (next) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const submit = async () => {
    setError(null);
    setInvalidField(null);
    // A described workout saves the tree the user just reviewed; the typed form
    // builds one from its rows. Both end in the same payload shape.
    const built = parsedPlan
      ? buildLoggedSessionFromPlan({
          plan: parsedPlan,
          title,
          startedAt,
          durationMinutes,
          rating,
          notes,
        })
      : buildLoggedSession({
          activityType,
          title,
          startedAt,
          durationMinutes,
          distanceKm,
          avgHr,
          rating,
          notes,
          // Weights typed in lb are saved in kg (#191).
          exercises: isStrength
            ? exercises.map((r) =>
                weightUnit === "lb" && r.weight !== "" && r.weight != null
                  ? { ...r, weight: String(fromDisplayWeight(r.weight, "lb") ?? r.weight) }
                  : r,
              )
            : [],
        });
    if (built.error) {
      setError(built.error);
      setInvalidField(built.field || null);
      fieldRefs[built.field]?.current?.focus();
      return;
    }

    setSaving(true);
    const failure = await onSave(built.payload);
    setSaving(false);
    if (failure) setError(failure);
    else close(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Log a past workout</DialogTitle>
          <DialogDescription>
            Training you did elsewhere. It counts toward your history and shapes
            what the planner suggests next.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1 pb-2">
          {!parsedPlan && (
            <ModeTabs
              value={mode}
              onChange={(next) => {
                setMode(next);
                setError(null);
                setInvalidField(null);
              }}
            />
          )}

          {mode === "describe" && !parsedPlan && (
            <div className="space-y-2">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                autoFocus
                placeholder={
                  "Say what you did, however you'd say it out loud.\n\n" +
                  "e.g. Three sets of 20 burpees, then bench press 80 kg " +
                  "for 8 reps six times, supersetted with deadlifts at " +
                  "110 kg for 8."
                }
                className="text-sm resize-none"
              />
              <Button
                onClick={interpret}
                disabled={parsing || !description.trim()}
                className="w-full gap-1.5"
              >
                {parsing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {!parsing
                  ? "Interpret"
                  : parseSlow
                    ? "Still reading — long one…"
                    : "Reading it…"}
              </Button>
              <p className="text-[11px] text-muted-foreground leading-snug">
                You'll get a chance to check and fix it before anything is
                saved.
              </p>
            </div>
          )}

          {mode === "form" && !parsedPlan && (
            <div className="space-y-1.5">
              <Label className={FIELD_LABEL_CLASS}>Activity</Label>
              <ActivityPicker value={activityType} onChange={setActivityType} />
            </div>
          )}

          {showDetails && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className={FIELD_LABEL_CLASS} htmlFor="log-when">
                When
              </Label>
              <Input
                id="log-when"
                ref={fieldRefs.startedAt}
                type="datetime-local"
                value={startedAt}
                max={toLocalInputValue(new Date())}
                onChange={(e) => {
                  setStartedAt(e.target.value);
                  if (invalidField === "startedAt") setInvalidField(null);
                }}
                aria-invalid={invalidField === "startedAt" || undefined}
                className={cn(
                  "h-9 text-sm",
                  invalidField === "startedAt" && INVALID_INPUT_CLASS,
                )}
              />
              {parsedPlan && (
                <p className="text-[10px] text-muted-foreground pt-0.5">
                  Read from your description
                </p>
              )}
              {/* Most logging happens a day or two late, so the common case is
                  one tap rather than a date picker. */}
              <div className="flex flex-wrap gap-1 pt-0.5">
                {DAY_CHIPS.map(({ days, label }) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => {
                      setStartedAt(daysAgoValue(days));
                      if (invalidField === "startedAt") setInvalidField(null);
                    }}
                    className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label className={FIELD_LABEL_CLASS} htmlFor="log-duration">
                Duration (min)
              </Label>
              <Input
                id="log-duration"
                ref={fieldRefs.duration}
                type="number"
                min="1"
                inputMode="numeric"
                value={durationMinutes}
                onChange={(e) => {
                  setDurationMinutes(e.target.value);
                  setDurationEstimated(false);
                  if (invalidField === "duration") setInvalidField(null);
                }}
                placeholder="45"
                aria-invalid={invalidField === "duration" || undefined}
                aria-describedby={
                  invalidField === "duration" ? "log-form-error" : undefined
                }
                className={cn(
                  "h-9 text-sm tabular-nums",
                  invalidField === "duration" && INVALID_INPUT_CLASS,
                )}
              />
              {/* The one figure a description rarely states, so say plainly
                  that it was inferred rather than passing a guess off as
                  something the user told us. */}
              {invalidField === "duration" ? (
                <p className="text-[10px] text-destructive pt-0.5">
                  Needed to save
                </p>
              ) : (
                durationEstimated && (
                  <p className="text-[10px] text-muted-foreground pt-0.5">
                    Estimated — adjust if you know it
                  </p>
                )
              )}
            </div>
          </div>
          )}

          {showDetails && (
          <div className="space-y-1">
            <Label className={FIELD_LABEL_CLASS} htmlFor="log-title">
              Name (optional)
            </Label>
            <Input
              id="log-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Push day, Parkrun, Tuesday spin…"
              className="h-9 text-sm"
            />
          </div>
          )}

          {parsedPlan && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className={FIELD_LABEL_CLASS}>
                  What we recorded
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-xs text-muted-foreground"
                  onClick={() => {
                    setParsedPlan(null);
                    setError(null);
                    setInvalidField(null);
                  }}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                  Redescribe
                </Button>
              </div>
              {/* Tap any row to correct it — the same editor the Session tab
                  uses, so a mis-read set count is fixed in place rather than
                  by starting over. */}
              <div className="rounded-lg border border-border/60 bg-surface/40 p-2">
                <SessionPlanDisplay
                  plan={parsedPlan}
                  editable
                  {...planDisplayProps}
                  llmSettings={llmSettings}
                />
              </div>
            </div>
          )}

          {showDetails && tracksDistance && !parsedPlan && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className={FIELD_LABEL_CLASS} htmlFor="log-distance">
                  Distance (km)
                </Label>
                <Input
                  id="log-distance"
                  type="number"
                  min="0"
                  step="0.1"
                  inputMode="decimal"
                  value={distanceKm}
                  onChange={(e) => setDistanceKm(e.target.value)}
                  placeholder="8.0"
                  className="h-9 text-sm tabular-nums"
                />
              </div>
              <div className="space-y-1">
                <Label className={FIELD_LABEL_CLASS} htmlFor="log-hr">
                  Avg HR (bpm)
                </Label>
                <Input
                  id="log-hr"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={avgHr}
                  onChange={(e) => setAvgHr(e.target.value)}
                  placeholder="148"
                  className="h-9 text-sm tabular-nums"
                />
              </div>
            </div>
          )}

          {showDetails && isStrength && !parsedPlan && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className={FIELD_LABEL_CLASS}>Exercises (optional)</Label>
                <UnitToggle value={weightUnit} onChange={setWeightUnit} />
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Naming the movements is the useful part. Figures are a bonus.
              </p>
              {exercises.map((row, i) => (
                <ExerciseRow
                  key={i}
                  row={row}
                  showHeadings={i === 0}
                  weightUnit={weightUnit}
                  canRemove={exercises.length > 1}
                  onChange={(next) =>
                    setExercises((prev) =>
                      prev.map((r, j) => (j === i ? next : r)),
                    )
                  }
                  onRemove={() =>
                    setExercises((prev) => prev.filter((_, j) => j !== i))
                  }
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground"
                onClick={() =>
                  setExercises((prev) => [...prev, emptyExercise()])
                }
              >
                <Plus className="h-3.5 w-3.5" />
                Add exercise
              </Button>
            </div>
          )}

          {showDetails && (
          <div className="space-y-1">
            <EffortMeter
              value={rating}
              onChange={setRating}
              compact
              label={<Label className={FIELD_LABEL_CLASS}>Effort (optional)</Label>}
            />
          </div>
          )}

          {showDetails && (
          <div className="space-y-1">
            <Label className={FIELD_LABEL_CLASS} htmlFor="log-notes">
              Notes (optional)
            </Label>
            <Textarea
              id="log-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="How did it feel? Anything the planner should know?"
              className="text-sm resize-none"
            />
          </div>
          )}

        </div>

        {/* Outside the scrolling body, directly above the button that caused
            it, so a refused save always says why without any scrolling. */}
        {error && (
          <p
            id="log-form-error"
            role="alert"
            className="text-xs text-destructive"
          >
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2 border-t border-border/60 pt-3 mt-1">
          <Button variant="ghost" onClick={() => close(false)} disabled={saving}>
            Cancel
          </Button>
          {showDetails && (
            <Button onClick={submit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Save to history
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogWorkoutDialog;
